import { ICRUDService } from '../../../reusableFeatures';
import { ERPGatewayConfigurationApi } from '../../../services/backendServices/Apis';
import {
    ERPGatewayConfigurationFormViewModel,
    ERPGatewayConfigurationViewModel,
    ERPGatewayConfigurationFormSchema,
} from '../../../services/backendServices/ViewModels/ERPGatewayConfigurationViewModel';
import { ERPViewModel } from '../../../services/backendServices/ViewModels/ERPViewModel';
import {
    mergeERPConnectionInfo,
    extractNonParameterFields,
    mergeParametersWithPreservedFields,
    convertERPConnectionInfoObjectToParams,
    ensureMandatoryConnectionInfoFields,
} from './ERPConnectionInfo.utils';
import { useMemo } from 'react';

export class ERPGatewayConfigurationCRUDService implements ICRUDService<number, ERPGatewayConfigurationFormViewModel> {
    private api = new ERPGatewayConfigurationApi();

    private baseERP?: ERPViewModel;

    private erpData: ERPViewModel[];

    private companyId?: number;

    // Store only the non-parameter fields to preserve backend-managed XML
    private preservedNonParameterFields: Record<string, any> = {};

    constructor(baseERP?: ERPViewModel, erpData: ERPViewModel[] = [], companyId?: number) {
        this.baseERP = baseERP;
        this.erpData = erpData;
        this.companyId = companyId ?? 0;
    }

    get defaultState(): ERPGatewayConfigurationFormViewModel {
        return {
            CompanyId: this.companyId ?? 0,
            InvoiceProfileId: null,
            BuyerDestinationProfileId: null,
            ERPName: this.baseERP?.ERPName ?? '',
            ERPVersion: this.baseERP?.ERPVersion ?? '',
            ERPConnectionInfo: ensureMandatoryConnectionInfoFields(this.baseERP?.ERPConnectionMetadata ?? null),
            Active: this.baseERP ? true : false,
            LastHealthCheck: null,
        };
    }

    get key() {
        return 'ERPGatewayConfiguration';
    }

    get invalidateKeys(): string[] {
        return ['ERPGatewayConfiguration'];
    }

    async fetchEntity(id: number): Promise<ERPGatewayConfigurationFormViewModel> {
        const response = await this.api.getERPGatewayConfiguration(id);
        const entity = response.data;

        // Extract and store non-parameter fields (LastReads, BackendField, etc.)
        this.preservedNonParameterFields = extractNonParameterFields(entity.ERPConnectionInfo);

        let mergedERPConnectionInfo = entity.ERPConnectionInfo;

        // Use baseERP if provided, otherwise search through erpData
        let matchingERP = this.baseERP;
        if (!matchingERP && this.erpData.length > 0 && entity.ERPName && entity.ERPVersion) {
            matchingERP = this.erpData.find((e) => e.ERPName === entity.ERPName && e.ERPVersion === entity.ERPVersion);
        }

        if (matchingERP && matchingERP.ERPConnectionMetadata) {
            mergedERPConnectionInfo = mergeERPConnectionInfo(
                matchingERP.ERPConnectionMetadata,
                entity.ERPConnectionInfo
            );
        }

        // Ensure mandatory fields (like ReprocessWorkflowForUploadErrors) are present post-merge
        mergedERPConnectionInfo = ensureMandatoryConnectionInfoFields(mergedERPConnectionInfo);

        return this.mapFromViewModel({ ...entity, ERPConnectionInfo: mergedERPConnectionInfo });
    }

    async updateEntity(id: number, state: ERPGatewayConfigurationFormViewModel): Promise<void> {
        const parsedForm = ERPGatewayConfigurationFormSchema.safeParse(state);

        if (!parsedForm.success) {
            let friendlyErrorMessage = '\nError(s) occurred while updating ERP Gateway Configuration:\n';

            const errors = parsedForm.error.errors;
            const formattedErrors = errors.map((error) => {
                const message = error.message;
                return `${message}`;
            });

            // format errors for user
            friendlyErrorMessage += formattedErrors.map((error) => `- ${error}`).join('\n');

            throw new Error(friendlyErrorMessage);
        }

        const viewModel = this.mapToViewModel(state, id);
        await this.api.putERPGatewayConfiguration(id, viewModel);
    }

    async createEntity(state: ERPGatewayConfigurationFormViewModel): Promise<number> {
        const parsedForm = ERPGatewayConfigurationFormSchema.safeParse(state);

        if (!parsedForm.success) {
            let friendlyErrorMessage = '\nError(s) occurred while creating ERP Gateway Configuration:\n';

            const errors = parsedForm.error.errors;
            const formattedErrors = errors.map((error) => {
                const message = error.message;
                return `${message}`;
            });

            // format errors for user
            friendlyErrorMessage += formattedErrors.map((error) => `- ${error}`).join('\n');

            throw new Error(friendlyErrorMessage);
        }

        const viewModel = this.mapToViewModel(state, null);
        const response = await this.api.postERPGatewayConfiguration(viewModel);

        return response.data.Id || 0;
    }

    async deleteEntity(): Promise<void> {
        throw new Error('not implemented');
    }

    private mapFromViewModel(viewModel: ERPGatewayConfigurationViewModel): ERPGatewayConfigurationFormViewModel {
        return {
            CompanyId: viewModel.CompanyID,
            InvoiceProfileId: viewModel.InvoiceProfileId,
            BuyerDestinationProfileId: viewModel.BuyerDestinationProfileId,
            ERPName: viewModel.ERPName,
            ERPVersion: viewModel.ERPVersion,
            ERPConnectionInfo: viewModel.ERPConnectionInfo,
            Active: viewModel.Active,
            LastHealthCheck: viewModel.LastHealthCheck,
        };
    }

    private mapToViewModel(
        formData: ERPGatewayConfigurationFormViewModel,
        id: number | null = null
    ): ERPGatewayConfigurationViewModel {
        // Extract parameters from form data
        const formParams = convertERPConnectionInfoObjectToParams(formData.ERPConnectionInfo);

        // Merge parameters with preserved non-parameter fields
        const preservedConnectionInfo = mergeParametersWithPreservedFields(
            formParams,
            this.preservedNonParameterFields
        );

        return {
            Id: id,
            CompanyID: formData.CompanyId,
            InvoiceProfileId: formData.InvoiceProfileId,
            BuyerDestinationProfileId: formData.BuyerDestinationProfileId,
            CreatedTime: new Date(),
            LastUpdateDate: new Date(),
            RowVersion: '',
            ERPName: formData.ERPName,
            ERPVersion: formData.ERPVersion,
            ERPConnectionInfo: preservedConnectionInfo,
            LastHealthCheck: formData.LastHealthCheck,
            Active: formData.Active,
        };
    }
}

export const useERPGatewayConfigurationCRUDService = (
    baseERP?: ERPViewModel,
    erpData: ERPViewModel[] = [],
    companyId?: number
) => {
    return useMemo(
        () => new ERPGatewayConfigurationCRUDService(baseERP, erpData, companyId),
        [baseERP, erpData, companyId]
    );
};
